import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export type AuditAction = 
  | "view"
  | "create"
  | "update"
  | "delete"
  | "download"
  | "export"
  | "access";

export type ResourceType =
  | "intake_form"
  | "patient_photo"
  | "insurance_card"
  | "session_notes"
  | "telehealth_session"
  | "patient_record"
  | "appointment";

interface AuditLogParams {
  action: AuditAction;
  resourceType: ResourceType;
  resourceId?: string;
  patientId?: string;
  details?: Record<string, unknown>;
}

export function useAuditLog() {
  const logEvent = useCallback(async ({
    action,
    resourceType,
    resourceId,
    patientId,
    details = {},
  }: AuditLogParams): Promise<string | null> => {
    try {
      const { data, error } = await supabase.rpc("log_audit_event", {
        p_action: action,
        p_resource_type: resourceType,
        p_resource_id: resourceId || null,
        p_patient_id: patientId || null,
        p_details: details as Json,
        p_ip_address: null,
        p_user_agent: navigator.userAgent,
      });

      if (error) {
        console.error("Failed to log audit event:", error);
        return null;
      }

      return data as string;
    } catch (err) {
      console.error("Audit logging error:", err);
      return null;
    }
  }, []);

  const logPatientRecordAccess = useCallback(async (
    patientId: string,
    recordType: ResourceType,
    recordId?: string,
    additionalDetails?: Record<string, unknown>
  ) => {
    return logEvent({
      action: "view",
      resourceType: recordType,
      resourceId: recordId,
      patientId,
      details: {
        accessed_at: new Date().toISOString(),
        ...additionalDetails,
      },
    });
  }, [logEvent]);

  const logIntakeFormView = useCallback(async (
    intakeFormId: string,
    patientId: string,
    patientName?: string
  ) => {
    return logEvent({
      action: "view",
      resourceType: "intake_form",
      resourceId: intakeFormId,
      patientId,
      details: {
        patient_name: patientName,
        accessed_at: new Date().toISOString(),
      },
    });
  }, [logEvent]);

  const logDocumentDownload = useCallback(async (
    documentType: "patient_photo" | "insurance_card",
    patientId: string,
    documentPath?: string
  ) => {
    return logEvent({
      action: "download",
      resourceType: documentType,
      patientId,
      details: {
        document_path: documentPath,
        downloaded_at: new Date().toISOString(),
      },
    });
  }, [logEvent]);

  const logSessionNotesAccess = useCallback(async (
    sessionId: string,
    patientId: string,
    action: "view" | "create" | "update" = "view"
  ) => {
    return logEvent({
      action,
      resourceType: "session_notes",
      resourceId: sessionId,
      patientId,
      details: {
        accessed_at: new Date().toISOString(),
      },
    });
  }, [logEvent]);

  return {
    logEvent,
    logPatientRecordAccess,
    logIntakeFormView,
    logDocumentDownload,
    logSessionNotesAccess,
  };
}
